"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/app/_module/components/ui/button";
import {
  managedMarketingApi,
  type Campaign,
  type WorkflowStep,
} from "./managedMarketingApi";
import { errorMessage } from "./marketingHelpers";
import s from "./MarketingWorkspace.module.css";

const firstMessage: WorkflowStep = {
  delayMinutes: 0,
  text: "Hi {{first_name}}, this is {{agent_name}}. Thanks for your insurance inquiry through Licensedproducer. When would be a good time to talk? Reply STOP to opt out.",
};

export default function CampaignFollowup({
  campaign,
  ownerId,
  operator,
  available,
  a2pReady,
}: {
  campaign: Campaign;
  ownerId: string;
  operator: boolean;
  available: boolean;
  /** A2P brand + campaign approved. The worker refuses to send without it. */
  a2pReady: boolean;
}) {
  const client = useQueryClient();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const runs = useQuery({
    queryKey: [
      "managed-marketing",
      ownerId,
      "workflow-runs",
      operator,
      campaign.id,
    ],
    queryFn: () => managedMarketingApi.workflowRuns(campaign.id, operator),
    enabled: expanded,
    staleTime: 15_000,
    retry: 1,
  });
  const cancel = useMutation({
    mutationFn: (runId: string) =>
      managedMarketingApi.cancelWorkflowRun(campaign.id, runId),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["managed-marketing", ownerId] }),
  });
  const published = campaign.workflowPublished;
  return (
    <section
      className="border-t p-6"
      aria-labelledby={`followup-${campaign.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id={`followup-${campaign.id}`} className="font-semibold">
            After an inquiry
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {published
              ? `${published.steps.length} text message${published.steps.length === 1 ? "" : "s"} · ${!a2pReady ? "Locked until A2P approval" : !available ? "Delivery is not enabled" : published.paused ? "Paused" : campaign.status === "live" ? "Active" : "Starts when your approved campaign is live"}`
              : "No automatic follow-up is attached to this campaign."}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls={`followup-content-${campaign.id}`}
        >
          {expanded
            ? "Hide follow-up"
            : operator
              ? "Manage follow-up"
              : "View follow-up"}
        </Button>
      </div>
      {expanded && !a2pReady && (
        /* The worker refuses to send without an approved A2P brand + campaign
           (managed-marketing-workflow.service a2pApproved), so building a
           follow-up here would just queue texts that never leave. Lock it and
           point at the one screen that fixes it. Operators are not exempt: the
           check is on the producer's own registration. */
        <div id={`followup-content-${campaign.id}`} className="mt-5">
          <div className={s.notice}>
            <p className="font-medium">
              Automatic texts are locked until A2P is approved.
            </p>
            <p className="my-2">
              Carriers require an approved A2P brand and campaign before a
              business can text consumers. Until yours is approved, follow-up
              messages cannot be sent{operator ? " for this producer" : ""}.
            </p>
            {!operator && (
              <Button variant="outline" onClick={() => router.push("/settings?tab=a2p-verification")}>
                Set up A2P messaging
              </Button>
            )}
          </div>
        </div>
      )}
      {expanded && a2pReady && (
        <div id={`followup-content-${campaign.id}`} className="mt-5 space-y-5">
          <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
            New inquiries can receive your approved messages through your CRM
            number. Follow-up stops when they reply, opt out, or book an
            appointment. Existing contacts are not enrolled again. Normal CRM
            texting charges apply separately from your ad budget.
          </p>
          {published && (
            <ol className="max-w-3xl divide-y rounded-lg border px-4">
              {published.steps.map((step, index) => (
                <li key={index} className="py-4">
                  <p className="text-xs text-muted-foreground">
                    Text {index + 1} ·{" "}
                    {step.delayMinutes === 0
                      ? "After the inquiry"
                      : `${step.delayMinutes.toLocaleString()} minutes after ${index === 0 ? "the inquiry" : "the previous text"}`}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">
                    {step.text}
                  </p>
                </li>
              ))}
            </ol>
          )}
          {operator && (
            <WorkflowEditor
              key={`${campaign.id}-${campaign.workflowDraft?.version || 0}`}
              campaign={campaign}
              ownerId={ownerId}
            />
          )}
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold">Follow-up activity</h4>
            <Button
              variant="ghost"
              disabled={runs.isFetching}
              onClick={() => runs.refetch()}
            >
              Refresh
            </Button>
          </div>
          {runs.isPending ? (
            <p role="status" className="text-sm">
              Loading follow-up activity…
            </p>
          ) : runs.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage(runs.error)}
            </p>
          ) : (
            <ul className="max-h-72 overflow-y-auto overscroll-contain divide-y">
              {runs.data.runs.map((run) => (
                <li
                  key={run.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
                >
                  <div>
                    <p className="capitalize">
                      {run.status.replaceAll("_", " ")} · Sequence{" "}
                      {run.workflowVersion}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {new Date(run.createdAt).toLocaleString()}
                      {run.reason
                        ? ` · ${run.reason.replaceAll("_", " ")}`
                        : ""}
                    </p>
                  </div>
                  {operator &&
                    ["scheduled", "blocked", "needs_review"].includes(
                      run.status,
                    ) && (
                      <Button
                        variant="outline"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(run.id)}
                      >
                        Cancel follow-up
                      </Button>
                    )}
                </li>
              ))}
              {!runs.data.runs.length && (
                <li className="py-3 text-sm text-muted-foreground">
                  No inquiries have entered this sequence yet.
                </li>
              )}
            </ul>
          )}
          {cancel.isError && (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage(cancel.error)}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function WorkflowEditor({
  campaign,
  ownerId,
}: {
  campaign: Campaign;
  ownerId: string;
}) {
  const client = useQueryClient();
  const [steps, setSteps] = useState<WorkflowStep[]>(
    campaign.workflowDraft?.steps || [firstMessage],
  );
  const [preview, setPreview] = useState<{
    previews: WorkflowStep[];
    warnings: string[];
  } | null>(null);
  const [notice, setNotice] = useState("");
  const draft = campaign.workflowDraft;
  const dirty = JSON.stringify(steps) !== JSON.stringify(draft?.steps);
  const closed = ["completed", "rejected"].includes(campaign.status);
  const live = ["live", "pause_requested"].includes(campaign.status);
  const mutation = useMutation({
    mutationFn: async (action: "save" | "test" | "publish" | "pause") => {
      if (action === "save")
        return managedMarketingApi.saveWorkflow(campaign.id, steps);
      if (action === "pause")
        return managedMarketingApi.pauseWorkflow(campaign.id);
      if (!draft || dirty)
        throw new Error("Save these messages before testing or publishing.");
      if (action === "test") {
        const result = await managedMarketingApi.testWorkflow(
          campaign.id,
          draft.version,
        );
        setPreview(result);
        return result;
      }
      return managedMarketingApi.publishWorkflow(campaign.id, draft.version);
    },
    onSuccess: async (_, action) => {
      setNotice(
        action === "test"
          ? "Test passed. No messages were sent."
          : action === "publish"
            ? "Sequence attached. Prepare the campaign for the producer’s approval."
            : action === "pause"
              ? "Follow-up paused. Queued messages will not send."
              : "Draft saved. Test this version before publishing.",
      );
      await client.invalidateQueries({
        queryKey: ["managed-marketing", ownerId],
      });
    },
  });
  function update(index: number, change: Partial<WorkflowStep>) {
    setSteps(
      steps.map((step, i) => (i === index ? { ...step, ...change } : step)),
    );
    setPreview(null);
    setNotice("");
    mutation.reset();
  }
  return (
    <div className="max-w-3xl space-y-4 border-t pt-5">
      <h4 className="text-sm font-semibold">
        Message sequence · Draft {draft?.version || 1}
      </h4>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Up to three texts over seven days. Use {"{{first_name}}"},{" "}
        {"{{agent_name}}"}, and {"{{profile_url}}"}. Include {"{{agent_name}}"}{" "}
        and “Reply STOP to opt out.” in every message. Delivery respects the
        CRM’s calling hours and texting eligibility.
      </p>
      {steps.map((step, index) => (
        <fieldset
          key={index}
          disabled={mutation.isPending || closed}
          className="space-y-3 rounded-lg border p-4"
        >
          <legend className="px-1 text-sm font-medium">Text {index + 1}</legend>
          <label className={s.label}>
            Wait before this text (minutes)
            <input
              className={s.field}
              type="number"
              min={index ? 15 : 0}
              max={10080}
              step={1}
              value={step.delayMinutes}
              onChange={(e) =>
                update(index, { delayMinutes: Number(e.target.value) })
              }
            />
          </label>
          <label className={s.label}>
            Message
            <textarea
              className={s.field}
              rows={3}
              maxLength={480}
              value={step.text}
              onChange={(e) => update(index, { text: e.target.value })}
            />
          </label>
          {steps.length > 1 && (
            <Button
              variant="ghost"
              onClick={() => {
                setSteps(steps.filter((_, i) => i !== index));
                setPreview(null);
              }}
            >
              Remove text
            </Button>
          )}
        </fieldset>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={mutation.isPending || closed || steps.length >= 3}
          onClick={() => {
            setSteps([...steps, { delayMinutes: 1440, text: "" }]);
            setPreview(null);
          }}
        >
          Add text
        </Button>
        <Button
          variant="outline"
          disabled={mutation.isPending || closed || !dirty}
          onClick={() => mutation.mutate("save")}
        >
          Save draft
        </Button>
        <Button
          variant="outline"
          disabled={mutation.isPending || closed || dirty || !draft}
          onClick={() => mutation.mutate("test")}
        >
          Test without sending
        </Button>
        <Button
          className={s.primary}
          disabled={
            mutation.isPending ||
            closed ||
            live ||
            dirty ||
            !draft ||
            draft.testedVersion !== draft.version
          }
          onClick={() => mutation.mutate("publish")}
        >
          Publish for approval
        </Button>
        {campaign.workflowPublished && !campaign.workflowPublished.paused && (
          <Button
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate("pause")}
          >
            Pause follow-up
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Publishing replaces the sequence and requires the producer to approve
        the campaign again.
        {live ? " Pause the ad campaign before publishing changes." : ""}
      </p>
      {mutation.isPending && (
        <p role="status" className="text-sm">
          Saving…
        </p>
      )}
      {mutation.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(mutation.error)}
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm">
          {notice}
        </p>
      )}
      {preview && (
        <div className="space-y-3 rounded-lg bg-muted p-4">
          <h5 className="text-sm font-medium">Test preview</h5>
          {preview.previews.map((step, i) => (
            <p key={i} className="whitespace-pre-wrap text-sm leading-relaxed">
              {step.text}
            </p>
          ))}
          {preview.warnings.map((warning) => (
            <p key={warning} className="text-xs text-muted-foreground">
              {warning}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
